import ApiModelWithAuth from "../../../models/ApiModelWithAuth";
import Formatter from "../../../models/Formatter";
import ApiAuth from "../../public/system/ApiAuth";

export class DeviceFormatter extends Formatter {
}

// Устройства (ReDroid): index/show/destroy + действия/ввод + батч-операции списка.
// Запросы с multipart или нестандартным ответом идут мимо JSON-конвейера ApiModel —
// через FetchWithAuth (Bearer из store, один повтор через ApiAuth.RefreshToken() при 401).
//
// Экрана/стрима/бенчмарка/установки приложений тут НЕТ: соответствующие эндпоинты сняты
// с API (routes/api.php), живой просмотр устройства теперь — отдельная страница
// control/{id}/live на стороне бэкенда, на неё ведёт колонка «Стрим» в списке.
export default class ApiDevice extends ApiModelWithAuth {
    pathDef = 'authorized/admin/devices';
    formatterItem = DeviceFormatter.Item;

    // POST-действие устройства через штатный механизм ApiModel (paths override).
    static __PostAction(path, params = {}) {
        const api = new this();
        api.__type = 'store';
        api.paths = { 'store': path };
        api.__params = params;
        api.formatterItem = null; // ответы действий — {ok, ...}, не запись
        return api;
    }

    // Прокси, которые можно назначить устройству (GET devices/{id}/proxy-options): только доступные его
    // ноде — без группы или из групп ноды, плюс текущий (is_current), даже если уже недоступен.
    // Списком, в осмысленном порядке (регион, адрес): [{id, label, proxy_group_id, available, is_current}].
    static ProxyOptions(id) {
        const api = new this();
        api.__type = 'index';
        api.__responseIsArray = true;
        api.paths = { 'index': `authorized/admin/devices/${id}/proxy-options` };
        api.formatterItem = null; // готовые подписи с бэка, не записи устройства
        return api;
    }

    // Действие над контейнером: action ∈ start|stop|restart|reset. Асинхронно: ставит задачу и сразу
    // отвечает {result, execute_id}; такая же задача уже ждёт или идёт — 409 {result: false, error}.
    // Статус устройства меняет сама задача, когда отработает (ход — на странице «Задачи»).
    static Action(id, action) {
        return this.__PostAction(`authorized/admin/devices/${id}/action`, { action });
    }

    // Смена тарифа (пересборка под новые ресурсы, run.py update) → {ok, error}. Синхронно, долго.
    static ChangeType(id, deviceTypeId) {
        return this.__PostAction(`authorized/admin/devices/${id}/change-type`, { device_type_id: deviceTypeId });
    }

    // Интерактивный ввод: {type: tap|swipe|key|text, x, y, keycode, text…} → {ok}.
    static Input(id, payload) {
        return this.__PostAction(`authorized/admin/devices/${id}/input`, payload);
    }

    // Куда можно перенести данные устройства (GET devices/{id}/transfer-targets): активные пустые
    // устройства по имени — [{id, name, node_id, node_name, status, available, reason}]; available: false —
    // пустое, но сейчас не подходит (reason — почему).
    static TransferTargets(id) {
        const api = new this();
        api.__type = 'index';
        api.__responseIsArray = true;
        api.paths = { 'index': `authorized/admin/devices/${id}/transfer-targets` };
        api.formatterItem = null; // кандидаты, не записи устройства
        return api;
    }

    // Перенос данных устройства (аккаунт, прокси с режимом, exit-IP, часовой пояс) с источника на
    // приёмник; источник очищается, Roblox на нём останавливается (если устройство доступно) и
    // разлогинивается, на приёмнике ставится вход. Пачкой: pairs — [{from_id, to_id}], до 50, устройство —
    // не больше чем в одной паре. dry_run — только проверить. Ответ 200 всегда (если прошла валидация): {result, done, refused, dry_run,
    // pairs: [{result, from_id, to_id, moved?, error?, login_execute_id?, clear_execute_id?}]}.
    static BatchTransfer(pairs, { dry_run = false } = {}) {
        return this.__PostAction('authorized/admin/devices/batch/transfer', { pairs, dry_run });
    }

    // Ручное управление пачкой: вывести выбранные устройства из-под расписания слоя агентов
    // или вернуть обратно → {result, updated}. Поштучно тот же флаг правится через Update().
    static BatchManual(deviceIds, isManual) {
        return this.__PostAction('authorized/admin/devices/batch/manual', {
            device_ids: deviceIds,
            is_manual: isManual,
        });
    }

    // --- Батч-операции с чекбоксов списка устройств (DeviceScriptsController) ---------

    // Установка одного APK на N устройств — multipart, поэтому мимо JSON-конвейера ApiModel.
    static async BatchInstall(deviceIds, file) {
        const formData = new FormData();
        deviceIds.forEach((id) => formData.append('device_ids[]', id));
        formData.append('apk', file);
        const response = await this.FetchWithAuth('authorized/admin/devices/batch/install', {
            method: 'POST',
            body: formData,
        });
        return response.ok ? response.json() : null;
    }

    // То же батч-действие, но APK берётся из каталога панели (files, type_file=apk) — тело
    // обычное JSON-ное, файл через браузер оператора не гоняется. Это единственный рабочий путь
    // для APK больше upload_max_filesize: загрузка с ПК на таком файле обрывается ещё до PHP.
    // Отдельный метод, а не флаг в BatchInstall: источник ровно ОДИН из двух — backend вешает
    // на apk правило prohibits:file_id (DeviceBatchInstallRequest).
    static async BatchInstallStored(deviceIds, fileId) {
        const response = await this.FetchWithAuth('authorized/admin/devices/batch/install', {
            method: 'POST',
            body: { device_ids: deviceIds, file_id: fileId },
        });
        return response.ok ? response.json() : null;
    }

    // Запуск любого whitelisted adb-скрипта (см. DeviceScriptsController::SCRIPTS) на
    // N устройствах через очередь → {dispatched_device_ids, skipped_device_ids, skipped_reasons}.
    // skipped_reasons — карта device_id => текст причины (объектом, пустая приходит как {}):
    // занятое устройство батч не рвёт, а пропускает с объяснением, и показать его — дело фронта.
    // extraParams — напр. {link} для
    // restart-mm2-script (явная ссылка вместо .env-дефолта, см. DeviceBatchScriptRequest).
    static async BatchRunScript(deviceIds, scriptName, extraParams = {}) {
        const response = await this.FetchWithAuth(`authorized/admin/devices/batch/scripts/${scriptName}`, {
            method: 'POST',
            body: { device_ids: deviceIds, ...extraParams },
        });
        return response.ok ? response.json() : null;
    }

    // То же батч-действие, но REDROID-ONLY эндпоинт: каталог ищется только среди redroid-скриптов
    // (reboot/reset/update и т.п. через android-сервис). Имя чужой семьи здесь — 404, а не молчаливый
    // запуск device-скрипта; берём его для заведомо redroid-only UI (см. спецификацию §2.2).
    // Тело/ответ те же, что у BatchRunScript ({dispatched/skipped_device_ids, skipped_reasons}).
    static async BatchRunRedroidScript(deviceIds, scriptName, extraParams = {}) {
        const response = await this.FetchWithAuth(`authorized/admin/devices/batch/redroid-scripts/${scriptName}`, {
            method: 'POST',
            body: { device_ids: deviceIds, ...extraParams },
        });
        return response.ok ? response.json() : null;
    }

    // Каталог массовых действий (GET devices/batch-actions) — источник правды для панели: группы
    // [{key, title, actions: [{name, family, label, order, danger, link, immediate_allowed, quick,
    // confirm_count, endpoint, note}]}]. Те же правила проверяют батч-эндпоинты.
    static BatchActions() {
        const api = new this();
        api.__type = 'index';
        api.__responseIsArray = true;
        api.paths = { 'index': 'authorized/admin/devices/batch-actions' };
        api.formatterItem = null; // группы каталога, не записи устройства
        return api;
    }

    // Массовое действие из каталога: POST на его endpoint (devices/batch/…). Тело — по действию:
    // скрипты {device_ids, immediate, link?, confirm_count?}, proxy-clear {device_ids}. 422 — отказ
    // целиком с {error} (не из каталога, нет ссылки, неверный confirm_count…) → {ok, status, data}.
    static async BatchRun(endpoint, body) {
        const response = await this.FetchWithAuth(`authorized/admin/${endpoint}`, { method: 'POST', body });
        const data = await response.json().catch(() => null);
        return { ok: response.ok, status: response.status, data };
    }

    // Статусы последних запусков adb-скриптов пачкой (для polling) → {data: [...]}.
    static async ScriptExecutes(deviceIds) {
        const query = deviceIds.map((id) => `device_ids[]=${id}`).join('&');
        const response = await this.FetchWithAuth(`authorized/admin/devices/script-executes?${query}`);
        return response.ok ? response.json() : null;
    }

    // Аккаунты сервиса acc.rbx.effps.com (для привязки — колонка «Аккаунт») → {data: [{id, login, label_show, label_select, status, is_online, url, bound_device?}]}.
    static async Accounts(login = '') {
        const query = login ? `?login=${encodeURIComponent(login)}` : '';
        const response = await this.FetchWithAuth(`authorized/admin/devices/accounts${query}`);
        return response.ok ? response.json() : null;
    }

    // История нагрузки (CPU/RAM/SWAP, %) для страницы «Мониторинг» — точки за hours часов,
    // отсортированы по времени. Свой Index, не унаследованный ApiModel.Index(): базовый
    // ставит __responseIsArray нормально (ответ и правда список), но path/параметр hours
    // нестандартные (см. ApiTask за тем же приёмом, но с обратным выводом — там пришлось
    // как раз ОТКЛЮЧИТЬ __responseIsArray).
    static Performance(id, hours = 24) {
        const api = new this();
        api.__type = 'index';
        api.__responseIsArray = true;
        api.paths = { index: `authorized/admin/devices/${id}/performance` };
        api.__params = { hours };
        api.formatterItem = null; // точки {t,cpu,ram,...} — не запись устройства
        return api;
    }

    // --- Запросы мимо JSON-конвейера ApiModel --------------------------------

    // fetch с Bearer и одним повтором после refresh при 401. body — FormData (файлы) или
    // обычный объект (уйдёт как JSON); для FormData Content-Type НЕ ставим — браузер сам
    // проставит multipart-boundary.
    static async FetchWithAuth(path, { method = 'GET', signal, keepalive, body } = {}) {
        const api = new this();
        const isFormData = body instanceof FormData;
        const doFetch = () => fetch(api.getUrl(path), {
            method,
            signal,
            keepalive,
            headers: {
                'Authorization': `Bearer ${api.getAccessToken()}`,
                ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
            },
            body: body === undefined ? undefined : (isFormData ? body : JSON.stringify(body)),
        });
        let response = await doFetch();
        if (response.status === 401) {
            const isRefreshed = await ApiAuth.RefreshToken();
            if (isRefreshed) response = await doFetch();
        }
        return response;
    }
}
